import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { HttpStatusCodeEnum } from '@/api';
import { alertDialog } from '@/ui_v3';

type ServiceType = 'ai' | 'learningMaterial';

export const MAX_SIZE_MB: Record<ServiceType, number> = {
  ai: 30,
  learningMaterial: 20,
};

export const ALLOWED_EXTENSIONS: Record<ServiceType, string[]> = {
  ai: ['pdf', 'csv', 'txt', 'docx', 'pptx', 'hwp'],
  learningMaterial: ['pdf'],
};

export const useValidateFile = ({ service }: { service: ServiceType }) => {
  const { t } = useTranslation(['build', 'common']);

  return (file: File) => {
    let title = '';
    let description = '';
    let errorStatus: HttpStatusCodeEnum | null = null;

    const fileExt = file ? file.name.split('.').pop()?.toLowerCase() || '' : '';

    if (!file) {
      title = t('common:common.validateFile.requiredFile');
    } else if (file.size > MAX_SIZE_MB[service] * 1024 ** 2) {
      title = t('common:common.validateFile.confirmFileSize');
      description = t('common:common.validateFile.possibleMaxFileSize', {
        fileSize: MAX_SIZE_MB[service] + 'MB',
      });
      errorStatus = HttpStatusCodeEnum.PAYLOAD_TOO_LARGE_413;
    } else if (!ALLOWED_EXTENSIONS[service].includes(fileExt)) {
      title = t('common:common.validateFile.confirmFileExt');
      description = t('common:common.validateFile.possibleFileExt', {
        fileExt: ALLOWED_EXTENSIONS[service].join(', '),
      });
      errorStatus = HttpStatusCodeEnum.UNSUPPORTED_MEDIA_TYPE_415;
    }

    if (errorStatus) {
      switch (service) {
        case 'learningMaterial': {
          logger.track('upload_learningMaterial_error', {
            type: errorStatus,
            isClientError: true,
          });
          break;
        }
        case 'ai': {
          logger.track('ai_문제생성_파일_업로드_에러', {
            error_type: errorStatus,
            isClientError: true,
          });
          break;
        }
      }
    }

    if (title) {
      alertDialog.open({
        header: {
          title,
        },
        body: description,
      });
      return false;
    }

    return true;
  };
};
